import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractService } from '../commons/abstract.service';
import {
  ChannelBusinessProfileResponse,
  ChannelHealthCompanyRefreshResponse,
  ChannelHealthDetailResponse,
  ChannelHealthListResponse,
  ChannelHealthRefreshResponse,
  ChannelMetaUsageResponse,
  ChannelQualityResponse,
  ChannelSummaryResponse,
  UpdateChannelProfileRequest,
} from '../models/channel-health.model';
import { Channel, HybridUsageResponse } from '../models/channel.model';

/**
 * Channel health endpoints (SPEC-2026-08-channel-health, T-12…T-16).
 * Same base path as ChannelService — the health routes live on the channel controller.
 */
@Injectable({ providedIn: 'root' })
export class ChannelHealthService extends AbstractService<Channel> {

  constructor(private http: HttpClient) {
    super(http);
  }

  getURLBase(): string {
    return '/channel';
  }

  preCreate(obj: Channel): void {
    // Health endpoints never create channels.
  }

  preUpdate(obj: Channel): void {
    // Health endpoints never update channels through the CRUD path.
  }

  listHealth(): Observable<ChannelHealthListResponse> {
    return super.get('/health/list');
  }

  refreshCompany(): Observable<ChannelHealthCompanyRefreshResponse> {
    return super.post('/health/refresh', {});
  }

  getHealthDetail(idChannel: string, page = 0, limit = 50): Observable<ChannelHealthDetailResponse> {
    return super.get(`/health/${idChannel}?page=${page}&limit=${limit}`);
  }

  refreshChannel(idChannel: string): Observable<ChannelHealthRefreshResponse> {
    return super.post(`/health/${idChannel}/refresh`, {});
  }

  getQuality(idChannel: string): Observable<ChannelQualityResponse> {
    return super.get(`/quality/${idChannel}`);
  }

  getSummary(idChannel: string): Observable<ChannelSummaryResponse> {
    return super.get(`/summary/${idChannel}`);
  }

  /** Free service messages used this month (Meta allowance per number) + the other billed categories. */
  getMetaUsage(idChannel: string): Observable<ChannelMetaUsageResponse> {
    return super.get(`/meta-usage/${idChannel}`);
  }

  /** Hybrid channels: outbound by side, effective share and estimated saving of a month (RF-25). */
  getHybridUsage(idChannel: string, month?: string): Observable<HybridUsageResponse> {
    return super.get(`/hybrid-usage/${idChannel}${month ? `?month=${month}` : ''}`);
  }

  getProfile(idChannel: string): Observable<ChannelBusinessProfileResponse> {
    return super.get(`/profile/${idChannel}`);
  }

  updateProfile(idChannel: string, dto: UpdateChannelProfileRequest): Observable<ChannelBusinessProfileResponse> {
    return super.put(`/profile/${idChannel}`, dto);
  }

  /** JPEG only, ≤ 5 MB — the square crop to 640×640 happens before this call (RF-33). */
  updateProfilePhoto(idChannel: string, file: File | Blob, fileName = 'profile-640.jpg'): Observable<ChannelBusinessProfileResponse> {
    const form = new FormData();
    form.append('file', file, fileName);
    return super.post(`/profile/${idChannel}/photo`, form);
  }

  requestDisplayName(idChannel: string, newDisplayName: string): Observable<unknown> {
    return super.post(`/profile/${idChannel}/displayName`, { newDisplayName });
  }
}
